import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DevisList from '../components/devis/DevisList';
import DevisForm from '../components/devis/DevisForm';
import SingleDevis from '../components/devis/SingleDevis';
import DevisDeleteButton from '../components/devis/DevisDeleteButton';
import DevisUpdateForm from '../components/devis/DevisupdateForm';
import ItemsInDevisList from '../components/devis/ItemInDevisList';
import AddItemToDevisForm from '../components/devis/AddItemToDevisForm';
import UpdateItemInDevisForm from '../components/devis/updateItemInDevisForm';
import DeleteItemFromDevisButton from '../components/devis/DeleteItemFromDevisButton';
import UnsavedChangesGuard from '../utils/UnsavedChangesGuard';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import './styles/devis-animations.css'; // Import the CSS file we created

const DevisManagementPage = () => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedDevisId, setSelectedDevisId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const containerRef = useRef(null);

  const user = useSelector(state => state.user);
  const userPermissions = {
    isManager: user?.IsManager,
    isDevisManager: user?.EffectiveIsDevisManager ?? user?.IsDevisManager,
  };

  // Handler for when changes are detected in SingleDevis
  const handleChangesDetected = () => {
    setHasUnsavedChanges(true);
  };

  // Handler for when changes are saved in SingleDevis
  const handleChangesSaved = () => {
    setHasUnsavedChanges(false);
  };

  // Handler to check for unsaved changes before view change
  const handleViewChange = useCallback((newView, newItemId = null) => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Modif. non enregistrées',
        content: 'Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter sans sauvegarder ?',
        okText: 'Quitter',
        cancelText: 'Rester',
        onOk: () => {
          requestAnimationFrame(() => {
            setCurrentView(newView);
            if (newItemId !== null) {
              setSelectedItemId(newItemId);
            }
            setHasUnsavedChanges(false);
          });
        },
      });
    } else {
      // Use requestAnimationFrame for smoother transitions
      requestAnimationFrame(() => {
        setCurrentView(newView);
        if (newItemId !== null) {
          setSelectedItemId(newItemId);
        }
      });
    }
  }, [hasUnsavedChanges]);

  const handleViewItemInDevis = (itemId) => {
    handleViewChange('viewItemInDevis', itemId);
  };

  const handleSelectDevis = (id) => {
    setSelectedDevisId(id);
    handleViewChange('viewDevis');
  };

  const handleDevisCreated = (newDevisId) => {
    setSelectedDevisId(newDevisId);
    handleViewChange('viewDevis');
  };

  const handleConfirmNavigation = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  // Optimized animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.97,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0], // Optimized ease curve
      }
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      transition: {
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    }
  };

  // Function to render the current view
  const renderView = () => {
    switch (currentView) {
      case 'create':
        return <DevisForm onDevisCreated={handleDevisCreated} />;
      case 'update':
        return <DevisUpdateForm devisId={selectedDevisId} />;
      case 'delete':
        return <DevisDeleteButton devisId={selectedDevisId} onSuccess={() => handleViewChange('list')} />;
      case 'list':
        return <DevisList 
                 onSelectDevis={handleSelectDevis} 
                 userPermissions={userPermissions}
               />;
      case 'viewDevis':
        return selectedDevisId && (
          <SingleDevis 
            devisId={selectedDevisId} 
            onSelectItem={handleViewItemInDevis} 
            onChangeView={handleViewChange}
            userPermissions={userPermissions}
            onChangesSaved={handleChangesSaved}
            onChangesDetected={handleChangesDetected}
          />
        );
      case 'viewItemInDevis':
        return selectedDevisId && selectedItemId && (
          <>
            <UpdateItemInDevisForm refDevis={selectedDevisId} codeArt={selectedItemId} />
            <DeleteItemFromDevisButton refDevis={selectedDevisId} codeArt={selectedItemId} onSuccess={() => handleViewChange('viewDevis')} />
          </>
        );
      case 'addItem':
        return selectedDevisId && (
          <AddItemToDevisForm refDevis={selectedDevisId} onSuccess={() => handleViewChange('viewDevis')} />
        );
      case 'viewItems':
        return selectedDevisId && (
          <ItemsInDevisList refDevis={selectedDevisId} />
        );
      default:
        return null;
    }
  };

  return (
    <UnsavedChangesGuard
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmNavigation={handleConfirmNavigation}
    >
      <div className="devis-management-container" ref={containerRef}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            className="view-container"
            style={{
              width: '100%',
              height: '100%'
            }}
          >
            {renderView()}
          </motion.div>
        </AnimatePresence>
      </div>
    </UnsavedChangesGuard>
  );
};

export default DevisManagementPage;